"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { secureGet } from "@/lib/security/fetch-client";
import { Thermometer, TrendingUp, TrendingDown, Activity, RefreshCw } from "lucide-react";

// 情绪类型
type EmotionType = "joy" | "anger" | "sadness" | "anxiety" | "surprise" | "neutral";

interface ThermometerData {
  temperature: number;
  dominantEmotion: EmotionType;
  dominantEmotionName: string;
  dominantEmotionIcon: string;
  dominantEmotionColor: string;
  emotionDistribution: {
    emotion: EmotionType;
    name: string;
    percentage: number;
    color: string;
    count: number;
  }[];
  keywords: {
    word: string;
    count: number;
    category: string;
  }[];
  timestamp: string;
  topicCount: number;
  cached?: boolean;
  history?: {
    temperatureTrend: "rising" | "falling" | "stable";
    temperatureChange: number;
    previousTemperature: number | null;
    volatility: number;
  };
  analysisMethod: "ai" | "keyword" | "hybrid";
  topicEmotions?: {
    id: number;
    name: string;
    emotion: EmotionType;
    emotionName: string;
    emotionIcon: string;
    emotionColor: string;
    intensity: number;
  }[];
}

// 情绪图标映射（优化后，更具表现力）
const EMOTION_ICONS: Record<EmotionType, string> = {
  joy: "🎉",
  anger: "😤",
  sadness: "😔",
  anxiety: "😟",
  surprise: "🤯",
  neutral: "😶",
};

// 温度等级描述（情绪晴雨表隐喻）
function getTemperatureLevel(temp: number, emotion?: EmotionType): { 
  label: string; 
  color: string; 
  description: string;
  weather: string;
  friendlyText: string;
} {
  // 愤怒主导 + 高温 = 雷暴
  if (emotion === "anger" && temp >= 60) {
    return { 
      label: "雷暴", 
      color: "text-red-500", 
      description: "舆论情绪极度高涨",
      weather: "⛈️",
      friendlyText: "舆论场有点躁动"
    };
  }
  // 喜悦主导 + 高温 = 晴朗
  if (emotion === "joy" && temp >= 60) {
    return { 
      label: "晴朗", 
      color: "text-yellow-500", 
      description: "公众情绪积极",
      weather: "☀️",
      friendlyText: "今天大家心情不错"
    };
  }
  // 悲伤/焦虑主导 = 阴雨
  if ((emotion === "sadness" || emotion === "anxiety") && temp >= 20) {
    return { 
      label: "阴雨", 
      color: "text-blue-400", 
      description: "情绪偏向低落",
      weather: "🌧️",
      friendlyText: "今天有些沉重的话题"
    };
  }
  // 高温
  if (temp >= 80) return { 
    label: "沸腾", 
    color: "text-red-500", 
    description: "舆论情绪极度高涨",
    weather: "🔥",
    friendlyText: "舆论场热火朝天"
  };
  if (temp >= 60) return { 
    label: "火热", 
    color: "text-orange-500", 
    description: "公众情绪较为强烈",
    weather: "⛅",
    friendlyText: "话题讨论挺热烈"
  };
  // 中等温度
  if (temp >= 40) return { 
    label: "温和", 
    color: "text-yellow-500", 
    description: "情绪状态适中",
    weather: "⛅",
    friendlyText: "情绪相对平稳"
  };
  // 低温
  if (temp >= 20) return { 
    label: "平静", 
    color: "text-blue-500", 
    description: "舆论相对平稳",
    weather: "🌤️",
    friendlyText: "今天比较平静"
  };
  return { 
    label: "冷淡", 
    color: "text-gray-500", 
    description: "公众情绪较为平淡",
    weather: "🌫️",
    friendlyText: "没什么大新闻"
  };
}

// 温度计可视化组件
function ThermometerGauge({ temperature, emotion, color }: { temperature: number; emotion: EmotionType; color: string }) {
  const level = getTemperatureLevel(temperature, emotion);
  const rotation = (temperature / 100) * 180 - 90; // -90 到 90 度
  
  return (
    <div className="flex flex-col items-center">
      {/* 天气图标 */}
      <span className="text-4xl mb-2">{level.weather}</span>
      
      {/* 温度计圆形表盘 */}
      <div className="relative w-32 h-16 overflow-hidden">
        {/* 半圆背景 */}
        <div className="absolute inset-0 rounded-t-full bg-gradient-to-r from-blue-400 via-yellow-400 to-red-500" />
        
        {/* 刻度标记 */}
        <div className="absolute inset-0 flex justify-between px-2 pt-1">
          {[0, 25, 50, 75, 100].map((mark) => (
            <span key={mark} className="text-[10px] text-white font-medium">
              {mark}
            </span>
          ))}
        </div>
        
        {/* 指针 */}
        <div 
          className="absolute bottom-0 left-1/2 w-1 h-14 bg-gray-800 origin-bottom transition-transform duration-1000"
          style={{ transform: `translateX(-50%) rotate(${rotation}deg)` }}
        >
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-gray-800" />
        </div>
      </div>
      
      {/* 温度值 */}
      <div className="mt-4 text-center">
        <div className={`text-4xl font-bold ${level.color}`}>{temperature}°</div>
        <div className="text-sm text-muted-foreground mt-1">{level.label}</div>
      </div>
      
      {/* 通俗解读 */}
      <div className="mt-2 text-sm text-foreground font-medium">
        {level.friendlyText}
      </div>
      
      {/* 主导情绪 */}
      <div className="mt-3 flex items-center gap-2">
        <span className="text-2xl">{EMOTION_ICONS[emotion]}</span>
        <Badge style={{ backgroundColor: color, color: "white" }}>
          {level.description}
        </Badge>
      </div>
    </div>
  );
}

// 情绪分布条形图
function EmotionDistributionChart({ distribution }: { distribution: ThermometerData["emotionDistribution"] }) {
  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-muted-foreground mb-3">情绪分布</div>
      {distribution.map((item) => (
        <div key={item.emotion} className="flex items-center gap-2">
          <span className="text-lg w-6">{EMOTION_ICONS[item.emotion]}</span>
          <span className="text-sm w-12">{item.name}</span>
          <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full transition-all duration-500"
              style={{ 
                width: `${item.percentage}%`, 
                backgroundColor: item.color 
              }}
            />
          </div>
          <span className="text-sm text-muted-foreground w-10 text-right">{item.percentage}%</span>
        </div>
      ))}
    </div>
  );
}

// 关键词云
function KeywordCloud({ keywords }: { keywords: ThermometerData["keywords"] }) {
  if (keywords.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-4">
        暂无情绪关键词
      </div>
    );
  }
  
  // 根据词频计算字体大小
  const maxCount = keywords[0]?.count || 1;
  
  return (
    <div className="flex flex-wrap gap-2 justify-center">
      {keywords.slice(0, 12).map((keyword, index) => {
        const size = Math.max(12, Math.min(20, 12 + (keyword.count / maxCount) * 8));
        return (
          <Badge 
            key={index} 
            variant="secondary"
            style={{ fontSize: `${size}px` }}
            className="px-2 py-1"
          >
            {keyword.word}
          </Badge>
        );
      })}
    </div>
  );
}

// 加载骨架屏
function ThermometerSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-20" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex flex-col items-center">
            <Skeleton className="w-32 h-16 rounded-t-full" />
            <Skeleton className="h-10 w-16 mt-4" />
            <Skeleton className="h-4 w-24 mt-2" />
          </div>
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton className="w-6 h-6" />
                <Skeleton className="w-12 h-4" />
                <Skeleton className="flex-1 h-3" />
                <Skeleton className="w-10 h-4" />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <Skeleton key={i} className="h-6 w-16" />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// 错误状态
function ThermometerError({ onRetry }: { onRetry: () => void }) {
  return (
    <Card className="border-destructive">
      <CardContent className="py-8 text-center">
        <Activity className="w-12 h-12 mx-auto text-destructive mb-3" />
        <p className="text-muted-foreground">无法加载情绪温度计数据</p>
        <button 
          onClick={onRetry}
          className="mt-3 text-primary hover:underline text-sm flex items-center gap-1 mx-auto"
        >
          <RefreshCw className="w-3 h-3" />
          重试
        </button>
      </CardContent>
    </Card>
  );
}

// 紧凑版骨架屏
function ThermometerSkeletonCompact() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-28" />
          <Skeleton className="h-4 w-16" />
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="flex items-center gap-4">
          <Skeleton className="w-16 h-16 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-3 w-full" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// 紧凑版错误状态
function ThermometerErrorCompact({ onRetry }: { onRetry: () => void }) {
  return (
    <Card className="border-destructive">
      <CardContent className="py-6 text-center">
        <Activity className="w-8 h-8 mx-auto text-destructive mb-2" />
        <p className="text-sm text-muted-foreground">加载失败</p>
        <button 
          onClick={onRetry}
          className="mt-2 text-primary hover:underline text-xs"
        >
          重试
        </button>
      </CardContent>
    </Card>
  );
}

// 紧凑版组件
function ThermometerCompact({ data }: { data: ThermometerData }) {
  const level = getTemperatureLevel(data.temperature, data.dominantEmotion);
  
  // 获取有情绪的话题（非中性），最多显示5个
  const emotionalTopics = (data.topicEmotions || [])
    .filter(t => t.emotion !== "neutral")
    .slice(0, 5);
  
  return (
    <Card className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900/50 dark:to-slate-800/50">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="text-xl">{level.weather}</span>
            舆情天气
          </CardTitle>
          <span className="text-sm text-muted-foreground">
            {data.topicCount}条热搜
          </span>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        {/* 一句话解读 */}
        <div className="text-base font-medium mb-3">
          {level.friendlyText}
          <span className="text-muted-foreground ml-2">
            {EMOTION_ICONS[data.dominantEmotion]} {data.dominantEmotionName}主导
          </span>
        </div>
        
        {/* 代表性话题 */}
        {emotionalTopics.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-xs text-muted-foreground mb-1">情绪话题</div>
            {emotionalTopics.map((topic) => (
              <div key={topic.id} className="flex items-center gap-2 text-sm">
                <span>{topic.emotionIcon}</span>
                <span className="truncate flex-1">{topic.name}</span>
              </div>
            ))}
          </div>
        )}
        
        {/* 底部：温度 + 趋势 */}
        <div className="flex items-center justify-between mt-3 pt-2 border-t text-xs text-muted-foreground">
          <span>情绪温度 {data.temperature}°</span>
          {data.history && (
            <span>
              {data.history.temperatureTrend === "rising" ? "↑" : 
               data.history.temperatureTrend === "falling" ? "↓" : "→"} 
              {data.history.temperatureChange > 0 ? "+" : ""}{data.history.temperatureChange}°
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function SentimentThermometer({ compact = false }: { compact?: boolean }) {
  const [data, setData] = useState<ThermometerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    
    try {
      const result = await secureGet<ThermometerData>("/api/sentiment/thermometer");
      setData(result);
    } catch (err) {
      console.error("获取情绪温度计数据失败:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 紧凑模式
  if (compact) {
    if (loading) return <ThermometerSkeletonCompact />;
    if (error || !data) return <ThermometerErrorCompact onRetry={fetchData} />;
    return <ThermometerCompact data={data} />;
  }

  // 完整模式
  if (loading) {
    return <ThermometerSkeleton />;
  }

  if (error || !data) {
    return <ThermometerError onRetry={fetchData} />;
  }

  const level = getTemperatureLevel(data.temperature, data.dominantEmotion);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2 bg-gradient-to-r from-muted/50 to-muted">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Thermometer className="w-5 h-5 text-orange-500" />
            公众情绪晴雨表
          </CardTitle>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {data.cached && (
              <Badge variant="outline" className="text-xs">缓存</Badge>
            )}
            <span>基于 {data.topicCount} 条热搜</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* 左侧：温度计 */}
          <div className="flex flex-col items-center justify-center">
            <ThermometerGauge 
              temperature={data.temperature} 
              emotion={data.dominantEmotion}
              color={data.dominantEmotionColor}
            />
          </div>
          
          {/* 中间：情绪分布 */}
          <div>
            <EmotionDistributionChart distribution={data.emotionDistribution} />
          </div>
          
          {/* 右侧：关键词云 */}
          <div>
            <div className="text-sm font-medium text-muted-foreground mb-3">情绪关键词</div>
            <KeywordCloud keywords={data.keywords} />
          </div>
        </div>
        
        {/* 底部趋势提示 */}
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <span className="text-lg mr-1">{level.weather}</span>
              <span>{level.friendlyText}</span>
            </div>
            <span>更新于 {new Date(data.timestamp).toLocaleTimeString()}</span>
          </div>
          
          {/* 历史波动指标 */}
          {data.history && (
            <div className="mt-3 flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">趋势:</span>
                <Badge 
                  variant={data.history.temperatureTrend === "rising" ? "default" : data.history.temperatureTrend === "falling" ? "destructive" : "secondary"}
                  className="text-xs"
                >
                  {data.history.temperatureTrend === "rising" ? "上升" : data.history.temperatureTrend === "falling" ? "下降" : "稳定"}
                  {data.history.temperatureChange > 0 ? " +" : " "}{data.history.temperatureChange}°
                </Badge>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">波动率:</span>
                <span className={data.history.volatility > 10 ? "text-orange-500" : "text-green-500"}>
                  {data.history.volatility}
                </span>
              </div>
              {data.history.previousTemperature && (
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground">上次:</span>
                  <span>{data.history.previousTemperature}°</span>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
